// External Dependencies
import React, { RefObject, useState } from 'react';
import { FiBarChart, FiDownload, FiEdit, FiGrid } from 'react-icons/fi';
import { MdOutlineContentCopy } from 'react-icons/md';
import { IconButton, Tooltip, VStack } from '@chakra-ui/react';
import {
  DisplayAggregateQuestionDto,
  DisplayIndividualQuestionDto,
} from '@keyops-cep/api-client';

// Internal Dependencies
import { ANALYTICS_EVENT_NAME } from '../../utils/constants/analytics-constants';
import i18n from '../../utils/i18n';

import { VisualizationChartRef } from '.';

type ExpandedViewOptionsProps = {
  visualizationRef: RefObject<VisualizationChartRef>;
  question: DisplayAggregateQuestionDto | DisplayIndividualQuestionDto;
  displayDataTable?: boolean;
  toggleDataTableDisplay?: () => void;
  displayEditToggle?: boolean;
  toggleEditDisplay?: () => void;
};

const COPY_TEXT = i18n.t('visualization.options.copy');
const COPIED_TEXT = i18n.t('visualization.options.copied');

const ExpandedViewOptions = ({
  visualizationRef,
  question,
  displayDataTable,
  toggleDataTableDisplay,
  displayEditToggle,
  toggleEditDisplay,
}: ExpandedViewOptionsProps): JSX.Element => {
  const [tooltipText, setTooltipText] = useState<string>(COPY_TEXT);

  const onDownload = () => {
    window.analytics.track(
      ANALYTICS_EVENT_NAME.ENGAGEMENT_DETAILS.VISUALIZATION_DOWNLOAD,
      {
        engagementId: question.engagementId,
        questionId: question.id,
        position: question.position,
      }
    );
    if (visualizationRef.current && visualizationRef.current.onDownload) {
      visualizationRef.current.onDownload(question.label);
    }
  };

  const onCopyToClipboard = () => {
    window.analytics.track(
      ANALYTICS_EVENT_NAME.ENGAGEMENT_DETAILS.VISUALIZATION_COPY,
      {
        engagementId: question.engagementId,
        questionId: question.id,
        position: question.position,
      }
    );
    if (
      visualizationRef.current &&
      visualizationRef.current.onCopyToClipboard
    ) {
      visualizationRef.current.onCopyToClipboard();

      setTooltipText(COPIED_TEXT);
      setTimeout(() => setTooltipText(COPY_TEXT), 2000);
    }
  };

  const handleTooltipOpen = () => {
    if (tooltipText === COPIED_TEXT) {
      setTooltipText(COPY_TEXT);
    }
  };

  return (
    <VStack spacing={1} mt={2}>
      {/* Dispaly edit icon iff displayEditToggle is defined */}
      {displayEditToggle !== undefined && (
        <Tooltip
          label="Edit question"
          placement="left"
          hasArrow
          color="white"
          bg="keyops.blue"
        >
          <IconButton
            size="md"
            borderRightRadius="0"
            color="keyops.blue"
            aria-label="expanded-chart-edit"
            icon={<FiEdit />}
            onClick={toggleEditDisplay}
          />
        </Tooltip>
      )}
      <Tooltip
        label={i18n.t('visualization.options.export')}
        placement="left"
        hasArrow
        color="white"
        bg="keyops.blue"
      >
        <IconButton
          size="md"
          borderRightRadius="0"
          color="keyops.blue"
          aria-label="expanded-chart-download"
          icon={<FiDownload />}
          onClick={onDownload}
        />
      </Tooltip>
      <Tooltip
        label={tooltipText}
        placement="left"
        hasArrow
        color="white"
        bg="keyops.blue"
        closeOnClick={false}
        onOpen={handleTooltipOpen}
      >
        <IconButton
          size="md"
          borderRightRadius="0"
          color="keyops.blue"
          aria-label="expanded-chart-copy"
          icon={<MdOutlineContentCopy />}
          onClick={onCopyToClipboard}
        />
      </Tooltip>
      {displayDataTable !== undefined && (
        <Tooltip
          label={
            displayDataTable
              ? i18n.t('visualization.options.viewVisual')
              : i18n.t('visualization.options.viewDataTable')
          }
          placement="left"
          hasArrow
          color="white"
          bg="keyops.blue"
        >
          <IconButton
            size="md"
            borderRightRadius="0"
            color="keyops.blue"
            aria-label="expanded-visual-toggle"
            icon={displayDataTable ? <FiBarChart /> : <FiGrid />}
            onClick={toggleDataTableDisplay}
          />
        </Tooltip>
      )}
    </VStack>
  );
};

export default ExpandedViewOptions;
